/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// UI lib dependencies
import {
	Avatar,
	BackgroundImage,
	Box,
	Flex,
	Image,
	NumberFormatter,
	Skeleton,
	Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

// UI local dependencies
import HeaderSection from '../HeaderSection/HeaderSection';

// Styles
import classes from './ProjectsSection.module.css';

import { IconQuestionMark } from '@tabler/icons-react';

// Utils
import { getCountryName } from '@utils/countries';

// Types
import { TMeasurabilityProject } from '@global-types/api/api';
import { TParsedMesurabilityProject } from '@features/resources/measurabilities/types';

// Constants
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { getUnitLabel } from '@utils/units';
import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ProjectsSection = (props: {
	isLoading: boolean;
	data: TMeasurabilityProject[];
	onSeeAll: () => void;
}) => {
	const { isLoading, data, onSeeAll } = props;
	/* ********************************** HOOKS ********************************* */
	const { t } = useTranslation();

	const { checkRole } = useAuthorization();
	/* ***************************** RENDER HELPERS ***************************** */

	function getProjects() {
		if (isLoading)
			return (
				<Skeleton
					className={classes.containerProject}
					height={'35%'}
					radius="xl"
				/>
			);

		return (
			<Box className={classes.wrapper}>
				{data.length == 0 ? (
					<Text
						className={classes.emptyTSelectOptionsText}
					>
						{t('global.noData')}
					</Text>
				) : (
					data.map(
						(_project: TParsedMesurabilityProject) => (
							<Box
								key={_project.id}
								className={classes.containerProject}
							>
								<Box className={classes.containerPicture}>
									<BackgroundImage
										className={classes.picture}
										src={_project.cover}
									>
										{_project?.supportedBy && (
											<Flex justify="flex-end">
												<Image
													src={
														_project?.supportedBy?.logo
													}
													className={
														classes.logoOrganization
													}
													fallbackSrc="https://archive.org/download/placeholder-image/placeholder-image.jpg"
												/>
											</Flex>
										)}
									</BackgroundImage>
								</Box>
								<Box className={classes.containerInfos}>
									<Box
										className={classes.containerInfosLeft}
									>
										<Text className={classes.textTitle}>
											{_project.name}
										</Text>

										<Box
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '5px',
											}}
										>
											<Avatar
												className={classes.logoCountry}
												radius={'xl'}
												size={'xs'}
												src={`https://flagcdn.com/w40/${_project?.location?.countryCode?.toLowerCase()}.png`}
											>
												<IconQuestionMark />
											</Avatar>

											<Flex gap={'5px'} align="center">
												<Text
													className={
														classes.textCountryProject
													}
												>
													{getCountryName(
														_project?.location
															?.countryCode || ''
													)}
												</Text>
												<Text
													className={
														classes.textCountryPlaceProject
													}
												>
													{` / ${
														_project?.location?.place ||
														''
													}`}
												</Text>
											</Flex>
										</Box>

										<Text
											className={classes.textDescription}
										>
											{t(
												PROJECT_TECHNOLOGIES.find(
													(technology) =>
														technology.value ==
														_project.technology
												)?.label || ''
											)}
										</Text>
									</Box>
									<Box
										className={
											classes.containerInfosRight
										}
									>
										<Text className={classes.textStatus}>
											{t('global.tokenAvailable')}
										</Text>
										<Text className={classes.textValue}>
											<NumberFormatter
												thousandSeparator=" "
												value={_project.tokenAvailable}
												suffix={
													' ' + getUnitLabel('tco2')
												}
												decimalScale={3}
											/>
										</Text>
									</Box>
								</Box>
							</Box>
						)
					)
				)}
			</Box>
		);
	}

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<HeaderSection
				title={
					checkRole([
						ROLES.PRODUCER_ADMIN,
						ROLES.PRODUCER_STAFF,
					])
						? t('global.myProjects')
						: t('global.newProjects')
				}
				onSeeAll={onSeeAll}
			/>
			{getProjects()}
		</Box>
	);
};
